import React from "react";
import { RadarAvatar, RadarCrest } from "../agent-radar/RadarAtoms";
import { radarShortPosition } from "../agent-radar/radarFormat";
import { clubStatusMeta } from "../../constants/clubRadarChoices";
import { countryToFlag } from "../../utils/countryFlag";
// Direct submodule imports (not the ./v2 barrel) — the barrel pulls in
// v2/overlays which itself imports ensureRadarAnim from this file; going
// straight to pills/avatars keeps the module graph cycle-free.
import { ShareBadge } from "./v2/pills";
import { AvatarStack } from "./v2/avatars";

// Shared visual atoms for the Club Radar surface (person-first identity, status
// pill, share-tier badge, origin/attribution lines), pixel-matched to the design
// (frames 1a-1q, 2a-2d). Reuses the agent-radar RadarAvatar/RadarCrest and the
// position/date formatters via import (Scope Law: no agent-radar file changed).

// Injects the prototype's pop/sheet keyframes once — shared id with the agent
// radar so a single <style> serves both (no-op if already present).
export const ensureRadarAnim = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("tp-radar-anim")) return;
  const el = document.createElement("style");
  el.id = "tp-radar-anim";
  el.textContent =
    "@keyframes tp-pop{from{transform:scale(0.96);opacity:0}to{transform:none;opacity:1}}" +
    "@keyframes tp-sheet{from{transform:translateY(48px);opacity:0}to{transform:none;opacity:1}}";
  document.head.appendChild(el);
};

// Country flag badge. Prefers an explicit flag from the API (emoji or image url),
// else derives an emoji from the nationality name/code. Renders nothing unknown.
export const Flag = ({ nationality, flag, size = 14 }) => {
  if (flag && /^https?:\/\//.test(flag)) {
    return <img src={flag} alt="" style={{ width: size, height: size, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} referrerPolicy="no-referrer" />;
  }
  const emoji = flag && !/^https?:\/\//.test(flag) ? flag : countryToFlag(nationality);
  if (!emoji) return null;
  return <span style={{ fontSize: size, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>;
};

// Small position pill beside a player name (e.g. "CB").
export const PositionPill = ({ position }) => {
  const pos = radarShortPosition(position);
  if (!pos) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "#EEF2F7",
        color: "#475467",
        borderRadius: 5,
        padding: "1px 6px",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.02em",
        flexShrink: 0,
      }}
    >
      {pos}
    </span>
  );
};

// Colour status pill (R7). tint bg + strong same-hue text + dot. Optional
// attribution ("by <name>") shown muted beside it (list rows, frame 1a).
export const StatusPill = ({ status, by }) => {
  const m = clubStatusMeta(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          border: `1px solid ${m.border}`,
          background: m.bg,
          color: m.text,
          borderRadius: 6,
          padding: "1px 8px",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: "17px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.dot, display: "inline-block" }} />
        {m.label}
      </span>
      {by && (
        <span style={{ fontSize: 11, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          by {by}
        </span>
      )}
    </span>
  );
};

// Person-first identity block: avatar, name + position pill, second line
// club crest + club · age + nationality flag. `size` scales the avatar.
export const PersonCell = ({ player = {}, size = 40 }) => {
  const name = player.name || "Player";
  const clubName = player.club_name || player.club || "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
      <RadarAvatar name={name} imageUrl={player.image_url} size={size} seed={player.initials || name} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </span>
          <PositionPill position={player.position} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2, minWidth: 0 }}>
          {clubName && <RadarCrest name={clubName} logoUrl={player.club_crest_url} size={14} seed={clubName} />}
          <span style={{ fontSize: 11.5, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[clubName, player.age].filter((x) => x !== null && x !== undefined && x !== "").join(" · ")}
          </span>
          <Flag nationality={player.nationality} flag={player.nationality_flag} size={13} />
        </div>
      </div>
    </div>
  );
};

// Overlapping avatar row (shared-list member preview, frames 1b meta row / 2c).
// items: [{initials, name, avatar_url?}]. Max 4 shown, then "+N". V2: thin
// adapter over the kit AvatarStack (artboard circles with colored member
// pairs, border 2px #fff, negative overlap) — same props contract as before.
export const AvatarRow = ({ items = [], total, size = 22 }) => (
  <AvatarStack
    members={items.map((m) => ({
      id: m.id,
      name: m.name || m.initials,
      initials: m.initials,
      imageUrl: m.avatar_url,
    }))}
    size={size}
    total={total}
  />
);

// Share-tier badge for a list (frames 1b/2c): "Personal" / "Shared · N people"
// (with avatar row) / "Shared with club". Compact variant drops the avatar row.
// V2: kit ShareBadge pills (r999, `#F3F4F6`/`#4B5563` neutral · `#E8F1FF`/
// `#1B6FE0` shared) — text composed from REAL state, counts never baked in.
export const ShareTierBadge = ({ list, compact = false }) => {
  const tier = list.share_tier || "personal";
  if (tier === "personal") {
    return <ShareBadge tone="neutral">Personal</ShareBadge>;
  }
  if (tier === "club") {
    return <ShareBadge tone="shared">Shared with club</ShareBadge>;
  }
  // selected
  const n = list.shared_member_count ?? (list.shared_member_preview || []).length;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <ShareBadge tone="shared">{`Shared · ${n} ${n === 1 ? "person" : "people"}`}</ShareBadge>
      {!compact && list.shared_member_preview?.length > 0 && (
        <AvatarRow items={list.shared_member_preview} total={n} />
      )}
    </span>
  );
};

// "Created by <name>" line (always shown per R4). `is_me` → "you". V2 styling
// per frame 1b meta row: 12px `#6B7280`, name 700 `#374151`.
export const CreatedByLine = ({ createdBy }) => {
  if (!createdBy) return null;
  const who = createdBy.is_me ? "you" : createdBy.name;
  return (
    <span style={{ fontSize: 12, color: "#6B7280" }}>
      Created by <span style={{ color: "#374151", fontWeight: 700 }}>{who}</span>
    </span>
  );
};
